import { useEffect, useRef } from 'react';
import { useAppSelector } from '@/shared/store/hooks';
import { selectIsAuthenticated } from '@/shared/store/slices/authSlice';
import { useRefreshTokenMutation } from '@/features/auth/api/tokenApi';

/**
 * Proactively refresh access token every 10 minutes
 *
 * Why this matters:
 * - Backend access token TTL is hardcoded to 15 minutes from creation
 * - Without refresh, user gets 401 mid-activity after 15 minutes
 * - By refreshing every 10 minutes, we extend the token before expiry
 * - Only logs out if truly idle AND refresh fails multiple times
 *
 * How it works:
 * 1. Starts a 10-minute interval when user logs in
 * 2. Every 10 minutes, calls POST /api/oauth/token
 * 3. Server validates refreshToken cookie and issues new accessToken via Set-Cookie
 * 4. Interval continues as long as user is authenticated
 * 5. Clears when user logs out or component unmounts
 * 6. Only runs when tab is visible (no waste on background tabs)
 */
export const useTokenRefreshInterval = () => {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const [refreshToken] = useRefreshTokenMutation();
  const intervalIdRef = useRef<number | null>(null);
  const isPageVisibleRef = useRef<boolean>(true);

  useEffect(() => {
    // Track tab visibility
    const handleVisibilityChange = () => {
      isPageVisibleRef.current = document.visibilityState === 'visible';
      // console.log(
      //   '[TokenRefresh] Tab visibility changed:',
      //   isPageVisibleRef.current
      // );
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      // User logged out - clear interval
      if (intervalIdRef.current) {
        // console.log(
        //   '[TokenRefresh] User logged out, clearing refresh interval'
        // );
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
      return;
    }

    // User is authenticated - start refresh interval
    // console.log(
    //   '[TokenRefresh] Starting token refresh interval (every 10 minutes)'
    // );

    const refreshAccessToken = async () => {
      // Only refresh if tab is visible
      if (!isPageVisibleRef.current) {
        // console.log('[TokenRefresh] Tab not visible, skipping refresh');
        return;
      }

      try {
        // console.log('[TokenRefresh] Refreshing access token...');
        await refreshToken({ grant_type: 'refresh_token' }).unwrap();
        // console.log('[TokenRefresh] Access token refreshed successfully');
      } catch {
        // console.error('[TokenRefresh] Failed to refresh token:', error);
        // Don't logout here - user can still retry or wait for explicit logout on 401
      }
    };

    // Start interval: refresh every 10 minutes (600,000 ms)
    intervalIdRef.current = setInterval(refreshAccessToken, 10 * 60 * 1000);

    // Also refresh immediately on mount (in case app was backgrounded)
    refreshAccessToken();

    return () => {
      if (intervalIdRef.current) {
        // console.log('[TokenRefresh] Clearing refresh interval on unmount');
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
    };
  }, [isAuthenticated, refreshToken]);
};
